import {
  type AudioRecordingResult,
  startAudioRecording as defaultStartAudioRecording,
} from './audio-recording-client.ts';
import { en } from './i18n/en.ts';
import type { SupportedLocale, WidgetMessages } from './i18n/index.ts';
import { showConsentModal, showRecordingOverlay } from './recording-flow.ts';

export interface AudioRecordingFlowOptions {
  shadow: ShadowRoot;
  workspaceName: string;
  retentionDays: number;
  startAudioRecording?: typeof defaultStartAudioRecording;
  onAuditEvent?: (event: {
    type: 'recording_consent_accepted' | 'recording_started' | 'recording_stopped';
    metadata?: Record<string, unknown>;
  }) => void;
  messages?: WidgetMessages;
  locale?: SupportedLocale;
}

export async function runAudioRecordingFlow(
  opts: AudioRecordingFlowOptions,
): Promise<AudioRecordingResult | null> {
  const consent = await showConsentModal({ ...opts, captureMode: 'audio' });
  if (!consent.accepted) return null;
  opts.onAuditEvent?.({ type: 'recording_consent_accepted' });
  let controller: Awaited<ReturnType<typeof defaultStartAudioRecording>>;
  try {
    controller = await (opts.startAudioRecording ?? defaultStartAudioRecording)();
  } catch {
    return null;
  }
  opts.onAuditEvent?.({ type: 'recording_started' });
  const stopped = await showRecordingOverlay(
    opts.shadow,
    controller,
    opts.messages ?? en,
    opts.locale,
  );
  if (!stopped) {
    controller.cancel();
    opts.onAuditEvent?.({ type: 'recording_stopped', metadata: { resolution: 'cancelled' } });
    return null;
  }
  const result = await controller.stop();
  opts.onAuditEvent?.({
    type: 'recording_stopped',
    metadata: {
      resolution: 'completed',
      duration_ms: result.durationMs,
      auto_stopped: result.autoStopped,
    },
  });
  return result;
}
